import {
  addMonths,
  addWeeks,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear
} from 'date-fns';
import {
  getFinanceTotals,
  getInvoiceRecordDate,
  getInvoiceRevenue,
  getRecordDate,
  toMoney
} from './helpers';

export const PERIODS = [
  { id: 'month', label: 'Bil' },
  { id: 'year', label: 'Sanad' }
];

const WEEK_STARTS_ON = 1;

const MONTHS_SO = [
  'Janaayo', 'Febraayo', 'Maarso', 'Abriil', 'Maajo', 'Juun',
  'Luuliyo', 'Agoosto', 'Sebtembar', 'Oktoobar', 'Nofembar', 'Diseembar'
];

const DAYS_SO = ['Axad', 'Isniin', 'Talaado', 'Arbaco', 'Khamiis', 'Jimco', 'Sabti'];

export const monthNameSo = (date) => MONTHS_SO[date.getMonth()];
export const dayNameSo = (date) => DAYS_SO[date.getDay()];

export const getPeriodBounds = (range, anchor) => {
  if (range === 'week') {
    return {
      start: startOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON }),
      end: endOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON })
    };
  }
  if (range === 'month') {
    return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  }
  if (range === 'year') {
    return { start: startOfYear(anchor), end: endOfYear(anchor) };
  }
  return { start: null, end: null };
};

export const shiftAnchor = (range, anchor, direction) => {
  if (range === 'week') return addWeeks(anchor, direction);
  if (range === 'month') return addMonths(anchor, direction);
  if (range === 'year') return addYears(anchor, direction);
  return anchor;
};

export const getPeriodLabel = (range, anchor) => {
  if (range === 'week') {
    const { start, end } = getPeriodBounds(range, anchor);
    return `${dayNameSo(start)} ${format(start, 'd')} – ${dayNameSo(end)} ${format(end, 'd')} ${monthNameSo(start)} ${format(start, 'yyyy')}`;
  }
  if (range === 'month') return `${monthNameSo(anchor)} ${format(anchor, 'yyyy')}`;
  if (range === 'year') return `Sanadka ${format(anchor, 'yyyy')}`;
  return 'Dhammaan xogta';
};

export const inPeriod = (items, start, end) => {
  if (!start || !end) return items;
  return items.filter((item) => {
    const date = getRecordDate(item);
    return date && isWithinInterval(date, { start, end });
  });
};

export const inPeriodInvoices = (items, start, end) => {
  if (!start || !end) return items;
  return items.filter((item) => {
    const date = getInvoiceRecordDate(item);
    return date && isWithinInterval(date, { start, end });
  });
};

export const getChangePercent = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

export const buildChartPoints = (range, start, end, invoices, expenses) => {
  if (range === 'year') {
    return eachMonthOfInterval({ start, end }).map((month) => {
      const bounds = { start: startOfMonth(month), end: endOfMonth(month) };
      const finance = getFinanceTotals(
        inPeriodInvoices(invoices, bounds.start, bounds.end),
        inPeriod(expenses, bounds.start, bounds.end)
      );
      return {
        key: format(month, 'yyyy-MM'),
        label: monthNameSo(month).slice(0, 3),
        fullLabel: monthNameSo(month),
        date: month,
        dakhli: finance.totalRevenue,
        kharash: finance.totalExpenses,
        faaido: finance.netProfit
      };
    });
  }

  return eachDayOfInterval({ start, end }).map((day) => {
    const bounds = { start: startOfDay(day), end: endOfDay(day) };
    const finance = getFinanceTotals(
      inPeriodInvoices(invoices, bounds.start, bounds.end),
      inPeriod(expenses, bounds.start, bounds.end)
    );
    return {
      key: format(day, 'yyyy-MM-dd'),
      label: format(day, 'd'),
      fullLabel: `${dayNameSo(day)} ${format(day, 'd')} ${monthNameSo(day)}`,
      date: day,
      dakhli: finance.totalRevenue,
      kharash: finance.totalExpenses,
      faaido: finance.netProfit
    };
  });
};

export const getServiceBreakdown = (invoices) => {
  const map = {};
  invoices.forEach((inv) => {
    const key = inv.serviceType || 'other';
    map[key] = (map[key] || 0) + getInvoiceRevenue(inv);
  });
  return Object.entries(map).map(([key, value]) => ({ key, value }));
};

export const buildSnapshotCards = (range, points) => {
  if (range === 'year') return points;

  return points.reduce((weeks, point) => {
    const weekStart = startOfWeek(point.date, { weekStartsOn: 1 });
    const key = format(weekStart, 'yyyy-MM-dd');
    const existing = weeks.find((week) => week.key === key);
    if (existing) {
      existing.dakhli += point.dakhli;
      existing.kharash += point.kharash;
      existing.faaido += point.faaido;
      return weeks;
    }
    weeks.push({
      key,
      date: weekStart,
      label: `${format(weekStart, 'd')} ${monthNameSo(weekStart).slice(0, 3)}`,
      fullLabel: `Toddobaad ${format(weekStart, 'd')} ${monthNameSo(weekStart)}`,
      dakhli: point.dakhli,
      kharash: point.kharash,
      faaido: point.faaido
    });
    return weeks;
  }, []);
};

export const getExpenseBreakdown = (expenses) => {
  const map = {};
  expenses.forEach((exp) => {
    const key = exp.category || 'kale';
    map[key] = (map[key] || 0) + toMoney(exp.amount);
  });
  return Object.entries(map).map(([key, value]) => ({ key, value }));
};
